import { produce } from 'immer'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  QuestionTreeSelector,
  type QuestionTreeSelectorProps,
} from '~/components/features/QuestionTreeSelector'
import { useFormField } from '~/hooks/useFormField'
import { findByPath } from '~/utils'

import {
  type SurveyGroupSurvey,
  useMainQuerySurveys,
} from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type AttachedQuestionSelectorProps = {
  index: number
  survey: SurveyGroupSurvey
}

export const AttachedQuestionSelector = ({
  index,
  survey,
}: AttachedQuestionSelectorProps) => {
  const { t } = useTranslation()
  const { control, getValues, setValue } = useFormContext<InputValues>()
  const field = useFormField(
    control,
    `questions.${index}.attachedQuestionMap.${survey.id}`,
  )

  const surveys = useMainQuerySurveys()
  const questions = React.useMemo(() => {
    const survey_ = findByPath(surveys, 'id', survey.id)

    return survey_?.questions ?? []
  }, [survey.id, surveys])

  const handleChange = React.useCallback<
    EH<QuestionTreeSelectorProps<(typeof questions)[number]>, 'onChange'>
  >(
    (questionId) => {
      field.onChange(questionId)
      const optionsName = `questions.${index}.options` as const
      const prevOptions = getValues(optionsName)
      setValue(
        optionsName,
        prevOptions.map(
          produce((draft) => {
            draft.attachedOptionMap[survey.id] = null
          }),
        ),
      )
    },
    [field, getValues, index, setValue, survey.id],
  )

  return (
    <LabelingContainer label={survey.title} labelVariant="small">
      <QuestionTreeSelector
        isMatrixQuestionSelectable={true}
        isNullSelectable={true}
        nullText={t('no_corresponding_questionnaire')}
        questions={questions}
        {...field}
        onChange={handleChange}
      />
    </LabelingContainer>
  )
}
